/*
 * Artisan's Avenue Workspace navigation (development).
 * Keeps the authenticated shell mounted on ordinary client pages.
 * The Playbook editor keeps full navigation to protect delayed autosaves.
 */
window.AAWorkspaceRouter = (() => {
  const routes = new Set([
    '/pages/workspace-dashboard',
    '/pages/workspace-previous-playbooks',
    '/pages/workspace-catalogs',
    '/pages/workspace-email',
    '/pages/workspace-account'
  ]);

  let shell = null;
  let sequence = 0;
  let controller = null;

  function validateUrl(href) {
    if (!shell?.root?.isConnected) return null;
    const context = window.AAWorkspaceContext?.get?.();
    if (!context?.user || context.authLocked || !context.clientId) return null;

    let url;
    try { url = new URL(href, window.location.origin); }
    catch { return null; }

    if (url.origin !== window.location.origin ||
        !routes.has(url.pathname) ||
        url.searchParams.has('playbook')) return null;

    if (context.isInternal) {
      const selected = window.AAAdminWorkspaceView?.getClientId?.();
      if (!selected || selected !== context.clientId) return null;
      const target = url.searchParams.get('aa_admin_client');
      if (target && target !== selected) return null;
      url.searchParams.set('aa_admin_client', selected);
    } else if (url.searchParams.has('aa_admin_client')) {
      return null;
    }

    const current = new URL(window.location.href);
    const previewId = current.searchParams.get('preview_theme_id');
    if (previewId && !url.searchParams.has('preview_theme_id')) {
      url.searchParams.set('preview_theme_id', previewId);
    }
    return url;
  }

  async function loadModules(path) {
    const context = window.AAWorkspaceContext?.get?.();
    if (!context?.user || !context.clientId) return;

    const safe = async (name, task) => {
      try { await task(); }
      catch (error) { console.error('Workspace page ' + name + ':', error); }
    };

    if (path === '/pages/workspace-dashboard') {
      await Promise.all([
        safe('dashboard', async () => {
          await shell.loadDashboard();
          await shell.loadDashboardAccountAlerts();
        }),
        safe('upcoming', () => shell.loadDashboardComingUp())
      ]);
    } else if (path === '/pages/workspace-previous-playbooks') {
      await safe('Playbook Library', () => shell.loadPreviousPlaybooks());
    } else if (path === '/pages/workspace-catalogs') {
      shell.catalogYearFilter = '';
      shell.catalogBrandFilter = '';
      shell.clientCoopPreview = null;
      await safe('catalogs', () => shell.loadClientModules());
      await safe('catalog history', () => shell.loadCatalogHistory());
    } else if (path === '/pages/workspace-email') {
      await safe('email access', () => shell.loadClientModules());
      await safe('email', () => window.AAEmailWorkspace?.init?.({
        root: shell.root,
        clientId: context.clientId,
        enabled: shell.hasClientEmailModule()
      }));
    } else if (path === '/pages/workspace-account') {
      window.AAClientTeamOwner?.mount?.(context);
      await Promise.all([
        safe('account', () => shell.loadAccount()),
        safe('marketing plan', () => shell.loadMarketingPlan()),
        safe('package', () => shell.loadMarketingPackage()),
        safe('connected accounts', () => shell.loadConnectedAccounts()),
        safe('contracts', () => shell.loadContractCenter()),
        safe('invoices', () => shell.loadInvoiceCenter()),
        safe('brand assets', () => shell.loadBrandAssets())
      ]);
    }
    shell.renderClientModuleNavigation();
  }

  async function navigate(url, addHistory) {
    const context = window.AAWorkspaceContext?.get?.();
    if (!context?.clientId || !validateUrl(url.href)) return;

    const ticket = ++sequence;
    controller?.abort();
    const attempt = new AbortController();
    controller = attempt;
    shell.root.querySelector('.aa-workspace__content')
      ?.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(url.href, {
        credentials: 'same-origin',
        signal: attempt.signal,
        headers: { Accept: 'text/html' }
      });
      if (!response.ok ||
          !String(response.headers.get('content-type') || '')
            .includes('text/html') ||
          new URL(response.url).pathname !== url.pathname) {
        throw new Error('Invalid Workspace page response');
      }

      const html = await response.text();
      if (attempt.signal.aborted || ticket !== sequence) return;

      const page = new DOMParser().parseFromString(html, 'text/html');
      const nextShell = page.querySelector(
        '[data-aa-workspace][data-aa-persistent-shell="v1"]'
      );
      const newContent = nextShell?.querySelector('.aa-workspace__content');
      const newTitle = nextShell?.querySelector('.aa-workspace__page-title');
      if (!newContent || !newTitle ||
          String(window.AAWorkspaceContext.get().clientId) !==
          String(context.clientId)) {
        throw new Error('Workspace destination or client could not be verified');
      }

      if (addHistory) {
        history.pushState({ ...(history.state || {}), aaWorkspaceRoute: true },
          '', url.pathname + url.search + url.hash);
      }

      const oldContent = shell.root.querySelector('.aa-workspace__content');
      oldContent.replaceWith(document.importNode(newContent, true));
      shell.root.querySelector('.aa-workspace__page-title').textContent =
        newTitle.textContent.trim();
      document.title = page.title || document.title;

      shell.root.querySelectorAll('.aa-workspace__nav-link').forEach(link => {
        link.classList.toggle('is-active',
          new URL(link.href, window.location.origin).pathname === url.pathname);
      });
      shell.closeSidebar();
      await loadModules(url.pathname);
    } catch (error) {
      if (attempt.signal.aborted || ticket !== sequence) return;
      console.error('Workspace navigation failed:', error);
      const content = shell.root.querySelector('.aa-workspace__content');
      if (content && !content.querySelector('[data-aa-route-error]')) {
        const warning = document.createElement('p');
        warning.dataset.aaRouteError = '';
        warning.setAttribute('role', 'alert');
        warning.textContent = 'That page could not load. Please try again.';
        content.prepend(warning);
      }
    } finally {
      if (ticket === sequence) {
        shell.root.querySelector('.aa-workspace__content')
          ?.removeAttribute('aria-busy');
      }
    }
  }

  function attach(instance) {
    if (shell || instance?.root?.dataset?.aaPersistentShell !== 'v1') return;
    shell = instance;

    // Keep the existing Shopify navigation for Playbook editing and
    // all routes outside this exact same-client allowlist.
    shell.root.addEventListener('click', event => {
      if (event.defaultPrevented || event.button !== 0 ||
          event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a[href]');
      if (!link || link.hasAttribute('download') ||
          (link.target && link.target !== '_self')) return;

      const url = validateUrl(link.href);
      if (!url) return;
      const current = new URL(window.location.href);
      if (current.pathname === url.pathname && current.search === url.search) {
        // Clicking the current tab must not trigger a full Shopify reload.
        if (!url.hash || current.hash === url.hash) event.preventDefault();
        return;
      }
      event.preventDefault();
      navigate(url, true);
    });

    window.addEventListener('popstate', () => {
      const url = validateUrl(window.location.href);
      if (url) navigate(url, false);
      else window.location.reload();
    });
  }

  return { attach };
})();
